/**
 * HTML Export Utility
 * 
 * Converts AI summary data into a downloadable HTML file with inline styling.
 * The HTML file includes all narrative content and key data tables.
 */

import { ESRI_CENSUS_YEAR, ESRI_PROJECTION_YEAR } from '../constants/esriDataVintage';

/**
 * Export AI summary to HTML file
 * @param {string} schoolName - Name of the school
 * @param {Object} geographicData - Geographic summary data
 * @param {Object} competitiveData - Competitive summary data
 * @param {Object} projectionsData - Projections summary data
 * @param {Object} parameters - Analysis parameters (driveTime, grades, etc.)
 * @param {Object} chartImages - Optional chart images as base64 data URLs
 * @param {boolean} isDropAPin - Whether this is a drop-a-pin location
 */
export const exportSummaryToHTML = (
  schoolName,
  geographicData,
  competitiveData,
  projectionsData,
  parameters,
  chartImages = {},
  isDropAPin = false
) => {
  const timestamp = new Date().toLocaleString();
  const sanitizedSchoolName = schoolName.replace(/[^a-z0-9]/gi, '_').toLowerCase();
  const filename = `${sanitizedSchoolName}_ai_summary_${Date.now()}.html`;
  
  const reportTitle = isDropAPin ? 'AI Geographic Location Analysis' : 'AI School Summary Report';

  const html = `
<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>AI Summary - ${schoolName}</title>
    <style>
        * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
        }
        
        body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica', 'Arial', sans-serif;
            line-height: 1.6;
            color: #1f2937;
            background-color: #f9fafb;
            padding: 40px 20px;
        }
        
        .container {
            max-width: 1200px;
            margin: 0 auto;
            background-color: white;
            padding: 40px;
            border-radius: 8px;
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }
        
        .header {
            border-bottom: 3px solid #673ab7;
            padding-bottom: 20px;
            margin-bottom: 30px;
        }
        
        h1 {
            color: #673ab7;
            font-size: 32px;
            margin-bottom: 10px;
        }
        
        .subtitle {
            color: #6b7280;
            font-size: 14px;
        }
        
        .parameters {
            background-color: #f3f4f6;
            padding: 15px;
            border-radius: 6px;
            margin-bottom: 30px;
        }
        
        .parameters h3 {
            font-size: 16px;
            margin-bottom: 10px;
            color: #374151;
        }
        
        .parameters p {
            margin: 5px 0;
            font-size: 14px;
            color: #6b7280;
        }
        
        .section {
            margin-bottom: 40px;
            padding: 20px;
            border: 1px solid #e5e7eb;
            border-radius: 6px;
        }
        
        .section-header {
            display: flex;
            align-items: center;
            margin-bottom: 20px;
            padding-bottom: 10px;
            border-bottom: 2px solid #e5e7eb;
        }
        
        .section-icon {
            width: 40px;
            height: 40px;
            background-color: #673ab7;
            color: white;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            margin-right: 12px;
            font-weight: bold;
            font-size: 18px;
        }
        
        .section-icon svg {
            width: 24px;
            height: 24px;
            fill: white;
        }
        
        h2 {
            color: #111827;
            font-size: 24px;
        }
        
        h3 {
            color: #374151;
            font-size: 18px;
            margin: 20px 0 10px 0;
        }
        
        .narrative {
            background-color: #f8fafc;
            padding: 20px;
            border-radius: 6px;
            margin-bottom: 20px;
            border-left: 4px solid #673ab7;
        }
        
        .narrative p {
            margin-bottom: 15px;
        }
        
        .narrative strong {
            color: #111827;
            font-weight: 600;
        }
        
        table {
            width: 100%;
            border-collapse: collapse;
            margin: 20px 0;
            font-size: 14px;
        }
        
        th, td {
            padding: 12px;
            text-align: left;
            border-bottom: 1px solid #e5e7eb;
        }
        
        th {
            background-color: #f9fafb;
            font-weight: 600;
            color: #374151;
        }
        
        tr:hover {
            background-color: #f9fafb;
        }
        
        .metric-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
            gap: 20px;
            margin: 20px 0;
        }
        
        .metric-card {
            background-color: #f9fafb;
            padding: 20px;
            border-radius: 6px;
            text-align: center;
        }
        
        .metric-value {
            font-size: 32px;
            font-weight: bold;
            color: #673ab7;
            margin: 10px 0;
        }
        
        .metric-label {
            font-size: 14px;
            color: #6b7280;
        }
        
        .badge {
            display: inline-block;
            padding: 4px 12px;
            border-radius: 12px;
            font-size: 12px;
            font-weight: 500;
        }
        
        .badge-success {
            background-color: #d1fae5;
            color: #065f46;
        }
        
        .badge-warning {
            background-color: #fef3c7;
            color: #92400e;
        }
        
        .badge-info {
            background-color: #dbeafe;
            color: #1e40af;
        }
        
        .positive {
            color: #10b981;
        }
        
        .negative {
            color: #ef4444;
        }
        
        .footer {
            margin-top: 40px;
            padding-top: 20px;
            border-top: 1px solid #e5e7eb;
            text-align: center;
            color: #6b7280;
            font-size: 12px;
        }
        
        @media print {
            body {
                background-color: white;
                padding: 0;
            }
            
            .container {
                box-shadow: none;
            }
            
            .section {
                page-break-inside: avoid;
            }
        }
    </style>
</head>
<body>
    <div class="container">
        <div class="header">
            <h1>${reportTitle}</h1>
            <div class="subtitle">
                ${schoolName}${isDropAPin ? ' (Geographic Location)' : ''}<br>
                Generated: ${timestamp}
            </div>
            ${isDropAPin ? '<p style="margin-top: 10px; color: #f59e0b; font-size: 14px;"><strong>Note:</strong> This is a drop-a-pin location for geographic market analysis. Enrollment projections and competitive analysis require enrollment data.</p>' : ''}
        </div>
        
        <div class="parameters">
            <h3>Analysis Parameters</h3>
            <p><strong>Drive Time:</strong> ${parameters.driveTime} minutes</p>
            <p><strong>Grades:</strong> ${parameters.grades.join(', ')}</p>
            <p><strong>Governance:</strong> ${parameters.governance.join(', ')}</p>
            ${parameters.schoolType && parameters.schoolType.length > 0 ? 
              `<p><strong>School Types:</strong> ${parameters.schoolType.join(', ')}</p>` : ''}
        </div>
        
        ${generateGeographicSection(geographicData, chartImages)}
        ${generateCompetitiveSection(competitiveData, chartImages)}
        ${generateProjectionsSection(projectionsData, chartImages)}
        
        <div class="footer">
            <p>This report was generated by the Education Data Collective AI Summary Tool</p>
            <p>Data is subject to change. Please verify critical information with source data.</p>
        </div>
    </div>
</body>
</html>
  `.trim();

  // Create blob and download
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.display = 'none';
  
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  
  URL.revokeObjectURL(url);
};

/**
 * SVG Icons for sections
 */
const ICON_SVG = {
  geographic: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/></svg>',
  competitive: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M16 11V3H8v6H2v12h20V11h-6zm-6-6h4v14h-4V5zm-6 6h4v8H4v-8zm16 8h-4v-6h4v6z"/></svg>',
  projections: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M16 6l2.29 2.29-4.88 4.88-4-4L2 16.59 3.41 18l6-6 4 4 6.3-6.29L22 12V6z"/></svg>'
};

/**
 * Generate Geographic section HTML
 */
function generateGeographicSection(data, chartImages = {}) {
  if (!data || data.error) {
    return `
      <div class="section">
        <div class="section-header">
          <div class="section-icon">${ICON_SVG.geographic}</div>
          <h2>Geographic Summary</h2>
        </div>
        <p style="color: #ef4444;">Geographic summary not available</p>
      </div>
    `;
  }

  const { data: geoData, narrative } = data;
  const population = geoData.population;

  return `
    <div class="section">
      <div class="section-header">
        <div class="section-icon">${ICON_SVG.geographic}</div>
        <h2>Geographic Summary</h2>
      </div>
      
      <div class="narrative">
        ${formatNarrative(narrative)}
      </div>
      
      ${chartImages.populationTrends ? `
        <div style="margin: 30px 0; text-align: center;">
          <h3 style="color: #673ab7; font-size: 18px; margin-bottom: 15px;">Population Trends</h3>
          <img 
            src="${chartImages.populationTrends}" 
            alt="Population Trends Chart" 
            style="max-width: 100%; height: auto; border: 1px solid #e5e7eb; border-radius: 8px; box-shadow: 0 2px 4px rgba(0,0,0,0.1);"
          />
        </div>
      ` : ''}
      
      ${chartImages.demographics ? `
        <div style="margin: 30px 0; text-align: center;">
          <h3 style="color: #673ab7; font-size: 18px; margin-bottom: 15px;">Community Demographics</h3>
          <img 
            src="${chartImages.demographics}" 
            alt="Demographics Chart" 
            style="max-width: 100%; height: auto; border: 1px solid #e5e7eb; border-radius: 8px; box-shadow: 0 2px 4px rgba(0,0,0,0.1);"
          />
        </div>
      ` : ''}
      
      ${population ? `
        <h3>Population Trends (Ages 5-17)</h3>
        <div class="metric-grid">
          <div class="metric-card">
            <div class="metric-label">${ESRI_CENSUS_YEAR} Population</div>
            <div class="metric-value">${population.population_2020.toLocaleString()}</div>
          </div>
          <div class="metric-card">
            <div class="metric-label">Current Population</div>
            <div class="metric-value">${population.population_current.toLocaleString()}</div>
          </div>
          <div class="metric-card">
            <div class="metric-label">${ESRI_PROJECTION_YEAR} Projection</div>
            <div class="metric-value">${population.population_2030.toLocaleString()}</div>
          </div>
          <div class="metric-card">
            <div class="metric-label">Change (${ESRI_CENSUS_YEAR}-Current)</div>
            <div class="metric-value ${population.change_past_pct >= 0 ? 'positive' : 'negative'}">
              ${population.change_past_pct > 0 ? '+' : ''}${population.change_past_pct}%
            </div>
          </div>
        </div>
      ` : ''}
      
      ${geoData.new_expanding_schools && geoData.new_expanding_schools.length > 0 ? `
        <h3>New or Expanding Schools</h3>
        <table>
          <thead>
            <tr>
              <th>School Name</th>
              <th>Governance</th>
              <th>Enrollment</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            ${geoData.new_expanding_schools.map(school => `
              <tr>
                <td>${school.name}</td>
                <td>${school.governance}</td>
                <td>${school.enrollment_current.toLocaleString()}</td>
                <td>
                  <span class="badge ${school.status === 'new' ? 'badge-success' : 'badge-info'}">
                    ${school.status === 'new' ? 'New School' : 'Expanding'}
                  </span>
                </td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      ` : ''}
    </div>
  `;
}

/**
 * Generate Competitive section HTML
 */
function generateCompetitiveSection(data, chartImages = {}) {
  if (!data || data.error) {
    return `
      <div class="section">
        <div class="section-header">
          <div class="section-icon">${ICON_SVG.competitive}</div>
          <h2>Competitive Summary</h2>
        </div>
        <p style="color: #ef4444;">Competitive summary not available</p>
      </div>
    `;
  }

  const { data: compData, narrative, sources } = data;
  const marketShare = compData.market_share;

  return `
    <div class="section">
      <div class="section-header">
        <div class="section-icon">${ICON_SVG.competitive}</div>
        <h2>Competitive Summary</h2>
      </div>
      
      <div class="narrative">
        ${formatNarrative(narrative)}
      </div>
      
      ${chartImages.marketShare ? `
        <div style="margin: 30px 0; text-align: center;">
          <h3 style="color: #673ab7; font-size: 18px; margin-bottom: 15px;">Market Share Analysis</h3>
          <img 
            src="${chartImages.marketShare}" 
            alt="Market Share Chart" 
            style="max-width: 100%; height: auto; border: 1px solid #e5e7eb; border-radius: 8px; box-shadow: 0 2px 4px rgba(0,0,0,0.1);"
          />
        </div>
      ` : ''}
      
      ${chartImages.focusSchools ? `
        <div style="margin: 30px 0; text-align: center;">
          <h3 style="color: #673ab7; font-size: 18px; margin-bottom: 15px;">Focus Schools Analysis</h3>
          <img 
            src="${chartImages.focusSchools}" 
            alt="Focus Schools Chart" 
            style="max-width: 100%; height: auto; border: 1px solid #e5e7eb; border-radius: 8px; box-shadow: 0 2px 4px rgba(0,0,0,0.1);"
          />
        </div>
      ` : ''}
      
      ${marketShare ? `
        <h3>Market Share Analysis</h3>
        <div class="metric-grid">
          <div class="metric-card">
            <div class="metric-label">Current Market Share</div>
            <div class="metric-value">${marketShare.market_share_current.toFixed(2)}%</div>
          </div>
          <div class="metric-card">
            <div class="metric-label">Market Share Change</div>
            <div class="metric-value ${marketShare.market_share_change >= 0 ? 'positive' : 'negative'}">
              ${marketShare.market_share_change > 0 ? '+' : ''}${marketShare.market_share_change.toFixed(2)}%
            </div>
          </div>
          <div class="metric-card">
            <div class="metric-label">Expected Share</div>
            <div class="metric-value">${marketShare.expected_market_share.toFixed(2)}%</div>
          </div>
          <div class="metric-card">
            <div class="metric-label">Status</div>
            <div class="metric-value" style="font-size: 18px;">
              ${marketShare.market_share_current > marketShare.expected_market_share ? 
                '<span class="positive">Above</span>' : 
                '<span class="negative">Below</span>'}
            </div>
          </div>
        </div>
      ` : ''}
      
      ${compData.focus_schools && compData.focus_schools.length > 0 ? `
        <h3>Focus Schools (Positive Market Share Growth)</h3>
        <table>
          <thead>
            <tr>
              <th>School Name</th>
              <th>Governance</th>
              <th>Enrollment</th>
              <th>Market Share</th>
              <th>Change</th>
            </tr>
          </thead>
          <tbody>
            ${compData.focus_schools.slice(0, 10).map(school => `
              <tr>
                <td>${school.name}</td>
                <td>${school.governance}</td>
                <td>${school.enrollment.toLocaleString()}</td>
                <td>${school.market_share_current}%</td>
                <td class="positive">+${school.market_share_change}%</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      ` : ''}
      
      ${sources && sources.length > 0 ? `
        <h3>Recent News & Developments</h3>
        ${sources.map(source => `
          <div style="margin: 15px 0; padding: 15px; background-color: #f9fafb; border-left: 3px solid #673ab7;">
            <strong>${source.title}</strong><br>
            <p style="margin: 10px 0; color: #6b7280;">${source.snippet}</p>
            <a href="${source.url}" target="_blank" style="color: #673ab7; text-decoration: none;">
              Read more →
            </a>
          </div>
        `).join('')}
      ` : ''}
    </div>
  `;
}

/**
 * Generate Projections section HTML
 */
function generateProjectionsSection(data, chartImages = {}) {
  if (!data || data.error) {
    return `
      <div class="section">
        <div class="section-header">
          <div class="section-icon">${ICON_SVG.projections}</div>
          <h2>Projections Summary</h2>
        </div>
        <p style="color: #ef4444;">Projections summary not available</p>
      </div>
    `;
  }

  const { data: projData, narrative } = data;
  const projections = projData.projections;

  let overallGrowthRate = null;
  if (projections?.historical?.length > 0 && projections?.projections?.length > 0) {
    const lastHistorical = projections.historical[projections.historical.length - 1].total;
    const lastProjection = projections.projections[projections.projections.length - 1].total;
    overallGrowthRate = ((lastProjection - lastHistorical) / lastHistorical * 100).toFixed(1);
  }

  return `
    <div class="section">
      <div class="section-header">
        <div class="section-icon">${ICON_SVG.projections}</div>
        <h2>Projections Summary</h2>
      </div>
      
      <div class="narrative">
        ${formatNarrative(narrative)}
      </div>
      
      ${chartImages.enrollmentProjections ? `
        <div style="margin: 30px 0; text-align: center;">
          <h3 style="color: #673ab7; font-size: 18px; margin-bottom: 15px;">5-Year Enrollment Projections</h3>
          <img 
            src="${chartImages.enrollmentProjections}" 
            alt="Enrollment Projections Chart" 
            style="max-width: 100%; height: auto; border: 1px solid #e5e7eb; border-radius: 8px; box-shadow: 0 2px 4px rgba(0,0,0,0.1);"
          />
        </div>
      ` : ''}
      
      ${projections?.historical?.length > 0 && projections?.projections?.length > 0 ? `
        <h3>Key Metrics</h3>
        <div class="metric-grid">
          <div class="metric-card">
            <div class="metric-label">Current Enrollment</div>
            <div class="metric-value">
              ${projections.historical[projections.historical.length - 1].total.toLocaleString()}
            </div>
          </div>
          <div class="metric-card">
            <div class="metric-label">5-Year Projection</div>
            <div class="metric-value">
              ${projections.projections[projections.projections.length - 1].total.toLocaleString()}
            </div>
          </div>
          <div class="metric-card">
            <div class="metric-label">Projected Change</div>
            <div class="metric-value ${parseFloat(overallGrowthRate) >= 0 ? 'positive' : 'negative'}">
              ${overallGrowthRate > 0 ? '+' : ''}${overallGrowthRate}%
            </div>
          </div>
        </div>
      ` : ''}
      
      ${projections?.projections?.length > 0 ? `
        <h3>Year-by-Year Projections (Median Scenario)</h3>
        <table>
          <thead>
            <tr>
              <th>Year</th>
              <th>Projected Enrollment</th>
              <th>Change from Previous</th>
              <th>% Change</th>
            </tr>
          </thead>
          <tbody>
            ${projections.projections.map((proj, index) => {
              const prevTotal = index === 0 && projections.historical?.length > 0
                ? projections.historical[projections.historical.length - 1].total
                : index > 0 ? projections.projections[index - 1].total : null;
              
              const change = prevTotal ? proj.total - prevTotal : null;
              const changePercent = prevTotal ? (change / prevTotal * 100).toFixed(1) : null;

              return `
                <tr>
                  <td><strong>${proj.year}</strong></td>
                  <td>${proj.total.toLocaleString()}</td>
                  <td class="${change >= 0 ? 'positive' : 'negative'}">
                    ${change !== null ? (change > 0 ? '+' : '') + change.toLocaleString() : 'N/A'}
                  </td>
                  <td>
                    ${changePercent !== null ? 
                      `<span class="badge ${parseFloat(changePercent) >= 0 ? 'badge-success' : 'badge-warning'}">
                        ${changePercent > 0 ? '+' : ''}${changePercent}%
                      </span>` : 
                      'N/A'}
                  </td>
                </tr>
              `;
            }).join('')}
          </tbody>
        </table>
      ` : ''}
    </div>
  `;
}

/**
 * Format narrative text with proper HTML paragraphs and markdown support
 */
function formatNarrative(narrative) {
  if (!narrative) return '<p>No narrative available</p>';
  
  // Handle string narratives (markdown format)
  if (typeof narrative === 'string') {
    return convertMarkdownToHTML(narrative);
  }
  
  // Handle structured narratives (parsed sections)
  if (narrative.sections) {
    let html = '';
    
    // Add executive summary if available
    if (narrative.executive_summary) {
      html += `<div style="margin-bottom: 20px;"><p>${narrative.executive_summary}</p></div>`;
    }
    
    // Add sections
    narrative.sections.forEach(section => {
      html += `
        <div style="margin-bottom: 20px;">
          <h3 style="color: #673ab7; margin-bottom: 10px;">${section.title}</h3>
          ${section.type === 'table' && section.competitors ? 
            generateCompetitorTable(section.competitors) : 
            convertMarkdownToHTML(section.content || '')}
        </div>
      `;
    });
    
    return html;
  }
  
  return '<p>No narrative available</p>';
}

/**
 * Convert markdown text to HTML
 */
function convertMarkdownToHTML(markdown) {
  if (!markdown) return '';
  
  let html = markdown;
  
  // Convert headers (## and ###)
  html = html.replace(/^### (.*?)$/gm, '<h4 style="color: #374151; font-size: 16px; margin: 15px 0 10px 0;">$1</h4>');
  html = html.replace(/^## (.*?)$/gm, '<h3 style="color: #673ab7; font-size: 18px; margin: 20px 0 10px 0;">$1</h3>');
  
  // Convert bold text
  html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
  
  // Convert italic text
  html = html.replace(/\*(.*?)\*/g, '<em>$1</em>');
  
  // Convert links [text](url)
  html = html.replace(/\[(.*?)\]\((.*?)\)/g, '<a href="$2" style="color: #673ab7; text-decoration: none;">$1</a>');
  
  // Split into blocks (paragraphs, lists, etc.)
  const blocks = html.split(/\n\n+/);
  
  const processedBlocks = blocks.map(block => {
    const trimmedBlock = block.trim();
    if (!trimmedBlock) return '';
    
    // Check if it's a header (already processed)
    if (trimmedBlock.startsWith('<h')) {
      return trimmedBlock;
    }
    
    // Check if it's a list (unordered or ordered) with potential nesting
    if (/^[-*]\s/.test(trimmedBlock) || /^\d+\.\s/.test(trimmedBlock)) {
      return processListBlock(trimmedBlock);
    }
    
    // Handle multi-line blocks that should be single paragraphs
    const singleLine = trimmedBlock.replace(/\n/g, ' ');
    return `<p style="margin-bottom: 15px; line-height: 1.7;">${singleLine}</p>`;
  }).filter(block => block);
  
  return processedBlocks.join('');
}

/**
 * Process a list block with support for nested items
 */
function processListBlock(block) {
  const lines = block.split('\n').filter(line => line.trim());
  const isOrdered = /^\d+\.\s/.test(lines[0]);
  const listTag = isOrdered ? 'ol' : 'ul';
  
  let html = `<${listTag} style="margin: 10px 0; padding-left: 30px;">`;
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const indent = line.match(/^(\s*)/)[0].length;
    
    // Check if this is a nested item (starts with spaces)
    if (indent >= 2) {
      // This is a nested item - extract content
      const content = line.trim().replace(/^[-*]\s+/, '').replace(/^\d+\.\s+/, '');
      
      // Check if we need to open a nested list
      const prevLine = i > 0 ? lines[i - 1] : '';
      const prevIndent = prevLine.match(/^(\s*)/)[0].length;
      
      if (prevIndent < indent) {
        // Start nested list
        html += `<${listTag} style="margin: 5px 0; padding-left: 25px;">`;
      }
      
      html += `<li style="margin-bottom: 6px; line-height: 1.6;">${content}</li>`;
      
      // Check if we need to close the nested list
      const nextLine = i < lines.length - 1 ? lines[i + 1] : '';
      const nextIndent = nextLine ? nextLine.match(/^(\s*)/)[0].length : 0;
      
      if (nextIndent < indent || i === lines.length - 1) {
        // Close nested list
        html += `</${listTag}>`;
      }
    } else {
      // This is a top-level item
      const content = line.replace(/^[-*]\s+/, '').replace(/^\d+\.\s+/, '');
      html += `<li style="margin-bottom: 8px; line-height: 1.6;">${content}</li>`;
    }
  }
  
  html += `</${listTag}>`;
  return html;
}

/**
 * Generate HTML table for competitors
 */
function generateCompetitorTable(competitors) {
  if (!competitors || competitors.length === 0) {
    return '<p style="color: #6b7280; font-style: italic;">No competing schools meeting the mandatory criteria were identified.</p>';
  }
  
  return `
    <table style="width: 100%; margin: 15px 0;">
      <thead>
        <tr>
          <th style="padding: 10px; text-align: left; background-color: #f9fafb; font-weight: 600;">School</th>
          <th style="padding: 10px; text-align: left; background-color: #f9fafb; font-weight: 600;">Enrollment</th>
          <th style="padding: 10px; text-align: left; background-color: #f9fafb; font-weight: 600;">Market Share</th>
          <th style="padding: 10px; text-align: left; background-color: #f9fafb; font-weight: 600;">Strategic Context</th>
        </tr>
      </thead>
      <tbody>
        ${competitors.map(comp => `
          <tr>
            <td style="padding: 10px; border-bottom: 1px solid #e5e7eb;"><strong>${comp.name}</strong></td>
            <td style="padding: 10px; border-bottom: 1px solid #e5e7eb;">${comp.enrollment}</td>
            <td style="padding: 10px; border-bottom: 1px solid #e5e7eb;">${comp.market_share}</td>
            <td style="padding: 10px; border-bottom: 1px solid #e5e7eb;">${convertMarkdownToHTML(comp.additional_info || '')}</td>
          </tr>
        `).join('')}
      </tbody>
    </table>
  `;
}

